import MetricsAPI from '../../../api/metrics';
import { types } from './mutations';
import { getPresetRange } from '../../../modules/metrics/datePresets';

// por conta: os filtros guardam ids de funis e usuários, que não existem nas
// outras contas e zerariam o dashboard sem nenhum filtro visível selecionado
function metricsFiltersKey(accountId, userId) {
  return `metrics_filters_pref_${accountId}_${userId}`;
}

export function loadPersistedMetricsFilters(accountId, userId) {
  try {
    const raw = localStorage.getItem(metricsFiltersKey(accountId, userId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function defaultMetricsFilters() {
  return {
    ...getPresetRange('l30'),
    datePreset: 'l30',
    pipelineIds: [],
    channels: [],
    assigneeIds: [],
  };
}

function persistMetricsFilters(accountId, userId, filters) {
  if (!userId) return;
  try {
    localStorage.setItem(
      metricsFiltersKey(accountId, userId),
      JSON.stringify(filters)
    );
  } catch {
    // ignora falha de persistência
  }
}

export default {
  setFilters({ commit, dispatch, rootGetters, state }, filters) {
    commit(types.SET_FILTERS, filters);
    persistMetricsFilters(
      rootGetters.getCurrentAccountId,
      rootGetters.getCurrentUser?.id,
      state.filters
    );
    dispatch('fetchOverview');
    dispatch('fetchFunnel');
    dispatch('fetchRankings');
    dispatch('fetchDistribution');
  },

  // troca rápida de filtro dispara múltiplos fetches em paralelo; como as
  // respostas podem chegar fora de ordem, cada fetch só aplica o commit se
  // ainda for a requisição mais recente para essa chave (evita sobrescrever
  // o estado com dados de um filtro já obsoleto).
  async fetchOverview({ commit, state }) {
    commit(types.BUMP_REQUEST_ID, 'overview');
    const requestId = state.requestIds.overview;
    commit(types.SET_UI_FLAG, { isLoadingOverview: true });
    try {
      const { data } = await MetricsAPI.getOverview(state.filters);
      if (requestId === state.requestIds.overview) {
        commit(types.SET_OVERVIEW, data);
      }
    } catch {
      // sem isso a tela segue mostrando os números do filtro anterior como se
      // fossem do filtro atual
      if (requestId === state.requestIds.overview) {
        commit(types.SET_OVERVIEW, null);
      }
    } finally {
      if (requestId === state.requestIds.overview) {
        commit(types.SET_UI_FLAG, { isLoadingOverview: false });
      }
    }
  },

  async fetchFunnel({ commit, state }) {
    commit(types.BUMP_REQUEST_ID, 'funnel');
    const requestId = state.requestIds.funnel;
    commit(types.SET_UI_FLAG, { isLoadingFunnel: true });
    try {
      const { data } = await MetricsAPI.getFunnel(state.filters);
      if (requestId === state.requestIds.funnel) {
        commit(types.SET_FUNNEL, data);
      }
    } catch {
      // sem isso a tela segue mostrando os números do filtro anterior como se
      // fossem do filtro atual
      if (requestId === state.requestIds.funnel) {
        commit(types.SET_FUNNEL, null);
      }
    } finally {
      if (requestId === state.requestIds.funnel) {
        commit(types.SET_UI_FLAG, { isLoadingFunnel: false });
      }
    }
  },

  async fetchRankings({ commit, state }) {
    commit(types.BUMP_REQUEST_ID, 'rankings');
    const requestId = state.requestIds.rankings;
    commit(types.SET_UI_FLAG, { isLoadingRankings: true });
    try {
      const { data } = await MetricsAPI.getRankings(state.filters);
      if (requestId === state.requestIds.rankings) {
        commit(types.SET_RANKINGS, data);
      }
    } catch {
      // sem isso a tela segue mostrando os números do filtro anterior como se
      // fossem do filtro atual
      if (requestId === state.requestIds.rankings) {
        commit(types.SET_RANKINGS, null);
      }
    } finally {
      if (requestId === state.requestIds.rankings) {
        commit(types.SET_UI_FLAG, { isLoadingRankings: false });
      }
    }
  },

  async fetchDistribution({ commit, state }) {
    commit(types.BUMP_REQUEST_ID, 'distribution');
    const requestId = state.requestIds.distribution;
    try {
      const { data } = await MetricsAPI.getDistribution(state.filters);
      if (requestId === state.requestIds.distribution) {
        commit(types.SET_DISTRIBUTION, data);
      }
    } catch {
      // sem isso a tela segue mostrando os números do filtro anterior como se
      // fossem do filtro atual
      if (requestId === state.requestIds.distribution) {
        commit(types.SET_DISTRIBUTION, null);
      }
    }
  },

  async triggerSync({ commit }) {
    commit(types.SET_SYNC_STATUS, { loading: true, error: null });
    try {
      const { data } = await MetricsAPI.triggerSync();
      commit(types.SET_SYNC_STATUS, {
        loading: false,
        queuedAt: data.queued_at,
        error: null,
      });
    } catch (e) {
      commit(types.SET_SYNC_STATUS, { loading: false, error: e.message });
    }
  },

  hydrateFiltersFromStorage({ commit, rootGetters }) {
    const saved = loadPersistedMetricsFilters(
      rootGetters.getCurrentAccountId,
      rootGetters.getCurrentUser?.id
    );
    // o state sobrevive à troca de conta: sem filtro salvo nesta conta, volta
    // ao padrão em vez de herdar funis e usuários da conta anterior
    let normalized = { ...defaultMetricsFilters(), ...saved };

    // preset relativo é recalculado para hoje; só período personalizado
    // mantém as datas salvas
    const presetRange = getPresetRange(normalized.datePreset);
    if (presetRange) normalized = { ...normalized, ...presetRange };

    commit(types.SET_FILTERS, normalized);
  },
};
